'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftTheme from 'src/hooks/useDriftTheme';
import { DriftTheme } from 'src/environmentVariables/EnvironmentVariableTypes';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import useCurrentSettings from 'src/hooks/useCurrentSettings';
import { useIsElfaWidgetEligible } from './hooks/useIsElfaWidgetEligible';
import { useElfaUserData } from './hooks/useElfaUserData';

// Elfa Widget Documentation: https://widget.elfa.ai/integration-guide

const ELFA_SCRIPT_URL = 'https://widget.elfa.ai/embed-apollo.js';
const ELFA_WIDGET_ORIGIN = 'https://widget.elfa.ai';
const ELFA_WIDGET_IFRAME_ID = 'elfa-apollo-iframe';
const ELFA_LAUNCHER_IFRAME_ID = 'elfa-apollo-launcher-iframe';
const ELFA_DRAG_HANDLE_ID = 'elfa-apollo-drag-handle';

const ELFA_MESSAGES = {
	WIDGET_READY: 'elfa.apollo.widget.ready',
	REAUTH_REQUIRED: 'elfa.apollo.auth.reauth_required',
	CHAT_MESSAGE_SENT: 'elfa.apollo.widget.chat_message_sent',
	AUTH_UPDATE: 'elfa.apollo.auth.update',
	CONTEXT_UPDATE: 'elfa.apollo.context.update',
} as const;

const ELFA_SOURCES = {
	WIDGET: 'elfa-apollo-widget',
	HOST: 'elfa-apollo-host',
} as const;

declare global {
	interface Window {
		__ELFA_APOLLO_INSTANCE_ID?: string;
	}
}

function mapTheme(driftTheme: string): 'dark' | 'light' {
	return driftTheme === DriftTheme.light ? 'light' : 'dark';
}

const ELFA_THEME_CONFIG = {
	dark: {
		colors:
			'{"accent":"#9d66ce","surfacePrimary":"#15121b","surfaceSecondary":"#030305","contentPrimary":"#c9c9d8","contentSecondary":"#6f708f"}',
		borderColor: '#3b3459',
	},
	light: {
		colors:
			'{"accent":"#8d4eaf","surfacePrimary":"#ffffff","surfaceSecondary":"#f1eefa","contentPrimary":"#131316","contentSecondary":"#6f708f"}',
		borderColor: '#ddd6f3',
	},
} as const;

function sendElfaMessage(type: string, payload: Record<string, unknown>) {
	const instanceId = window.__ELFA_APOLLO_INSTANCE_ID;
	if (!instanceId) return;
	window.postMessage(
		{
			type,
			source: ELFA_SOURCES.HOST,
			version: 1,
			instanceId,
			timestamp: Date.now(),
			payload,
		},
		window.location.origin
	);
}

async function fetchElfaToken(
	userIdentifier: string,
	userData?: Record<string, unknown>
): Promise<string | null> {
	const response = await fetch('/api/elfa/token', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			userIdentifier,
			userData,
		}),
	});

	if (!response.ok) {
		console.error('Failed to fetch Elfa token:', response.status);
		return null;
	}

	const data = await response.json();
	return data.userToken ?? null;
}

const ElfaWidget = () => {
	const currentAccount = useCurrentUserAccount();
	const selectedMarket = useDriftStore((s) => s.selectedMarket?.current);
	const theme = useDriftTheme();

	const usersAccountPubKey = currentAccount?.pubKey;
	const authority = currentAccount?.authority;

	const [{ showElfaWidget }] = useCurrentSettings();
	const shouldLoadWidget = useIsElfaWidgetEligible() && showElfaWidget;
	const userIdentifier = useMemo(
		() => usersAccountPubKey?.toBase58(),
		[usersAccountPubKey]
	);
	const elfaSymbol = selectedMarket?.uiSymbols.baseAssetSymbol ?? 'SOL';
	const elfaUserData = useElfaUserData(authority);

	const queryClient = useQueryClient();
	const scriptRef = useRef<HTMLScriptElement | null>(null);
	const [scriptLoaded, setScriptLoaded] = useState(false);

	const teardownWidget = useCallback(() => {
		if (scriptRef.current?.parentNode) {
			scriptRef.current.parentNode.removeChild(scriptRef.current);
		}
		scriptRef.current = null;
		setScriptLoaded(false);

		document.getElementById(ELFA_WIDGET_IFRAME_ID)?.remove();
		document.getElementById(ELFA_LAUNCHER_IFRAME_ID)?.remove();
		document.getElementById(ELFA_DRAG_HANDLE_ID)?.remove();
		delete window.__ELFA_APOLLO_INSTANCE_ID;
	}, []);

	const { data: userToken } = useQuery({
		queryKey: ['elfa-token', userIdentifier],
		queryFn: () =>
			userIdentifier && authority
				? fetchElfaToken(userIdentifier, elfaUserData)
				: null,
		enabled: shouldLoadWidget && authority && !!userIdentifier,
		staleTime: 10 * 60 * 1000, // 10 minutes
		retry: 1,
	});

	// Load script once we have a token
	useEffect(() => {
		if (!shouldLoadWidget || !userToken || scriptRef.current) return;

		const script = document.createElement('script');
		script.src = ELFA_SCRIPT_URL;
		script.async = true;
		const mappedTheme = mapTheme(theme);
		const themeConfig = ELFA_THEME_CONFIG[mappedTheme];

		script.setAttribute('data-elfa-embed', 'true');
		script.setAttribute('data-user-token', userToken);
		script.setAttribute('data-theme', mappedTheme);
		script.setAttribute('data-symbol', elfaSymbol);
		script.setAttribute('data-lookup-preference', 'symbol');
		script.setAttribute('data-chain', 'solana');
		script.setAttribute(
			'data-trusted-origins',
			window.location.origin + ',' + ELFA_WIDGET_ORIGIN
		);
		script.setAttribute('data-elfa-style', themeConfig.colors);
		script.setAttribute(
			'data-widget-style',
			`border-radius: 16px; font-family: Inter, sans-serif; border: 1px solid ${themeConfig.borderColor}`
		);

		script.onload = () => setScriptLoaded(true);
		script.onerror = () => console.error('Failed to load Elfa widget script');

		document.body.appendChild(script);
		scriptRef.current = script;

		return () => teardownWidget();
	}, [shouldLoadWidget, theme, teardownWidget, !!userToken]); // we don't want to re-load the script if the user token updates

	// Listen for widget postMessage events
	const handleReauth = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['elfa-token'] });
	}, [queryClient]);

	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			if (event.origin !== ELFA_WIDGET_ORIGIN) return;
			const { type, source } = event.data || {};
			if (source !== ELFA_SOURCES.WIDGET) return;

			switch (type) {
				case ELFA_MESSAGES.WIDGET_READY:
					console.log('elfa widget is ready');
					break;
				case ELFA_MESSAGES.REAUTH_REQUIRED:
				case ELFA_MESSAGES.CHAT_MESSAGE_SENT: // we reauth on chat message sent so that we can update the user data context
					handleReauth();
					break;
			}
		};

		window.addEventListener('message', handleMessage);
		return () => window.removeEventListener('message', handleMessage);
	}, [handleReauth]);

	// Send auth update when token refreshes after reauth
	useEffect(() => {
		if (!scriptLoaded || !userToken) return;

		sendElfaMessage(ELFA_MESSAGES.AUTH_UPDATE, { userToken });
	}, [userToken, scriptLoaded]);

	// Sync market context when market changes
	useEffect(() => {
		if (!scriptLoaded) return;

		sendElfaMessage(ELFA_MESSAGES.CONTEXT_UPDATE, {
			symbol: elfaSymbol,
			lookupPreference: 'symbol',
		});
	}, [elfaSymbol, scriptLoaded]);

	// Close widget panel on Escape key
	useEffect(() => {
		if (!scriptLoaded) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				const widgetIframe = document.getElementById(ELFA_WIDGET_IFRAME_ID);
				if (widgetIframe && widgetIframe.style.display !== 'none') {
					widgetIframe.style.display = 'none';
				}
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [scriptLoaded]);

	return null;
};

export default ElfaWidget;
